import { Identity } from './User'

export interface CrossClientQueryAtomicClause {
  variable: string
  description: string
  questionForm: string
}

export interface CrossClientQueryLogicalQuestionDecomposition {
  explanation: string
  logicalFormula: string
  atomicClauses: CrossClientQueryAtomicClause[]
}

export interface CrossClientQueryLogicalQuestionNNFDecomposition {
  nnfExplanation: string
  nnfLogicalFormula: string
  atomicClauses: CrossClientQueryAtomicClause[]
  negatedClauses: Record<string, string>
}

export const CrossClientQueryPhases = {
  NO_CLIENTS_WERE_FOUND: 'noClientsWereFound',
  NO_CLIENTS_AFTER_FILTER_IDS: 'noClientsAfterFilterIDs',
  NO_CLIENTS_AFTER_FILTER_ROLE: 'noClientsAfterFilterRole',
  QUESTION_ERROR: 'questionError',
  INITIALIZED: 'initialized',
  READY: 'ready',
  QUEUED: 'queued',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  CANCELED: 'canceled',
  ERRORED: 'errored',
} as const

export type CrossClientQueryPhase = (typeof CrossClientQueryPhases)[keyof typeof CrossClientQueryPhases]

export const CrossClientQueryResultStatuses = {
  INITIALIZED: 'initialized',
  PROCESSING: 'processing',
  SUCCEEDED: 'succeeded',
  NO_DATA: 'noData',
  FAILED: 'failed',
} as const

export type CrossClientQueryResultStatus = (typeof CrossClientQueryResultStatuses)[keyof typeof CrossClientQueryResultStatuses]

export const CrossClientQueryResultMatches = {
  MATCH: 'full',
  PARTIAL: 'partial',
  NO_MATCH: 'noMatch',
} as const

export type CrossClientQueryResultMatch = (typeof CrossClientQueryResultMatches)[keyof typeof CrossClientQueryResultMatches]

interface ClientProfileCrossClientQueryDataSource {
  type: 'clientProfile'
}

interface IntegrationCrossClientQueryDataSource {
  type: 'integration'
  integrationType: string
}

interface MeetingCrossClientQueryDataSource {
  type: 'meeting'
}

export type CrossClientQueryDataSource = ClientProfileCrossClientQueryDataSource | IntegrationCrossClientQueryDataSource | MeetingCrossClientQueryDataSource

export interface CrossClientQueryExecution {
  id: string
  createdAt: string
  updatedAt: string
  startedAt: string | null
  finishedAt: string | null
  canceledAt: string | null
  createdBy: Identity
  updatedBy: Identity
  phase: CrossClientQueryPhase
  clientIds: string[]
  resultCount: number
  sources: CrossClientQueryDataSource[]
  decomposition?: CrossClientQueryLogicalQuestionDecomposition
  nnfDecomposition?: CrossClientQueryLogicalQuestionNNFDecomposition
  userQuestion: string
}

export interface CrossClientQueryInitializedExecution {
  executionId: string
  phase: CrossClientQueryPhase
  decomposition?: CrossClientQueryLogicalQuestionDecomposition
  nnfDecomposition?: CrossClientQueryLogicalQuestionNNFDecomposition
}

export type CrossClientQueryDecision = boolean | null

export interface CrossClientQueryResultSource {
  source: CrossClientQueryDataSource
  retrievedAt: string | null
  metadata?: Record<string, unknown>
}

export interface CrossClientQueryResultAnswer {
  decision: CrossClientQueryDecision
  explanation: string
  source?: CrossClientQueryResultSource
}

export interface CrossClientQueryResult {
  id: string
  clientId: string
  status: CrossClientQueryResultStatus
  createdAt: string
  updatedAt: string
  answers?: CrossClientQueryResultAnswer[]
  match?: CrossClientQueryResultMatch
}

export interface CrossClientQueryResultDisplay extends CrossClientQueryResult {
  match: CrossClientQueryResultMatch
}

export interface CrossClientQueryExecutionProgressResponse {
  executionId: string
  phase: CrossClientQueryPhase
  status: CrossClientQueryResultStatus
  resultCount: number
  createdAt: string
  startedAt: string | null
  finishedAt: string | null
  canceledAt: string | null
  lastUpdatedAt: string
}

export interface CrossClientQueryExecutionProgress {
  executionId: string
  phase: CrossClientQueryPhase
  statuses: Record<CrossClientQueryResultStatus, number>
  startedAt: string | null
  finishedAt: string | null
  canceledAt: string | null
  lastUpdatedAt: string
}
